import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import Header from "@/components/layout/Header"
import Footer from "@/components/layout/Footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { signupMember, checkDuplicateId } from "@/api/authApi" // 중복체크 API 추가 필요

export default function SignupPage() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)

    // 💡 아이디 중복 체크 상태 관리
    const [isIdChecked, setIsIdChecked] = useState(false)
    const [passwordError, setPasswordError] = useState("")

    const [form, setForm] = useState({
        loginId: "",
        email: "",
        password: "",
        passwordConfirm: "",
        memberName: "",
        phone: "",
        zipcode: "",
        address1: "",
        address2: ""
    })

    // 💡 비밀번호 정규식 (영문, 숫자, 특수문자 조합 8~16자)
    const validatePassword = (password: string) => {
        const regex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,16}$/
        return regex.test(password)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target
        const key = id.replace("signup", "").replace(/^[A-Z]/, c => c.toLowerCase())

        setForm(prev => ({ ...prev, [key]: value }))

        // 아이디가 변경되면 중복체크 초기화
        if (key === "loginId") {
            setIsIdChecked(false)
        }

        // 비밀번호 실시간 유효성 검사
        if (key === "password") {
            if (!validatePassword(value)) {
                setPasswordError("영문, 숫자, 특수문자를 포함하여 8~16자로 입력해주세요.")
            } else {
                setPasswordError("")
            }
        }
    }

    // 💡 1. 아이디 중복 체크 로직
    const handleCheckId = async () => {
        if (!form.loginId) return alert("아이디를 입력해주세요.")
        if (form.loginId.length < 4) return alert("아이디는 4자 이상 입력해주세요.")

        try {
            // 실제 API 연동 시 아래 주석 해제 (true: 사용가능, false: 중복)
            const isAvailable = await checkDuplicateId(form.loginId)

            // 임시 테스트용 로직 (admin은 중복으로 간주)
            // const isAvailable = form.loginId !== "admin"

            if (isAvailable) {
                alert("사용 가능한 아이디입니다.")
                setIsIdChecked(true)
            } else {
                alert("이미 사용 중인 아이디입니다.")
                setIsIdChecked(false)
            }
        } catch (error) {
            alert("중복 확인 중 오류가 발생했습니다.")
        }
    }

    // 💡 3. 카카오/다음 우편번호 API 동적 로드 및 실행
    const handleSearchAddress = () => {
        const script = document.createElement("script")
        script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
        script.onload = () => {
            new (window as any).daum.Postcode({
                oncomplete: (data: any) => {
                    setForm(prev => ({
                        ...prev,
                        zipcode: data.zonecode,      // 우편번호
                        address1: data.address,      // 기본 주소
                        address2: ""                 // 주소 변경 시 상세주소 초기화
                    }))
                    // 주소 검색 후 상세주소 입력창으로 포커스 이동
                    document.getElementById("signupAddress2")?.focus()
                }
            }).open()
        }
        document.body.appendChild(script)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isIdChecked) {
            return alert("아이디 중복 확인을 진행해주세요.")
        }
        if (!validatePassword(form.password)) {
            return alert("비밀번호 생성 규칙을 확인해주세요.")
        }
        if (form.password !== form.passwordConfirm) {
            return alert("비밀번호가 일치하지 않습니다.")
        }
        if (!form.zipcode || !form.address1) {
            return alert("주소를 검색하여 입력해주세요.")
        }

        try {
            setLoading(true)
            await signupMember({
                loginId: form.loginId,
                password: form.password,
                memberName: form.memberName,
                email: form.email,
                phone: form.phone,
                zipcode: form.zipcode,
                address1: form.address1,
                address2: form.address2
            })

            alert("회원가입이 완료되었습니다!\n로그인 페이지로 이동합니다.")
            navigate("/login")
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || "회원가입에 실패했습니다."
            alert(errorMsg)
        } finally {
            setLoading(false)
        }
    }

    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
          <Header />
          <main className="mx-auto flex max-w-7xl items-center justify-center px-4 py-12 lg:px-6">
              <div className="w-full max-w-2xl">
                  <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                      <CardContent className="p-8">
                          <div className="text-center">
                              <h1 className="text-3xl font-bold">회원가입</h1>
                              <p className="mt-2 text-sm text-slate-500">
                                  엠지퀵플라워 회원으로 가입하고 다양한 혜택을 받아보세요.
                              </p>
                          </div>

                          <form onSubmit={handleSubmit} className="mt-8 grid gap-5 md:grid-cols-2">
                              {/* 아이디 영역 */}
                              <div className="space-y-2 md:col-span-2">
                                  <Label htmlFor="signupLoginId">아이디</Label>
                                  <div className="flex gap-2">
                                      <Input id="signupLoginId" value={form.loginId} onChange={handleChange} placeholder="아이디를 입력하세요" className="h-12 rounded-xl" required />
                                      <Button type="button" variant={isIdChecked ? "default" : "outline"} className={`h-12 rounded-xl shrink-0 ${isIdChecked ? "bg-green-600 hover:bg-green-700 text-white" : ""}`} onClick={handleCheckId}>
                                          {isIdChecked ? "확인 완료" : "중복 확인"}
                                      </Button>
                                  </div>
                              </div>

                              {/* 이메일 영역 */}
                              <div className="space-y-2 md:col-span-2">
                                  <Label htmlFor="signupEmail">이메일</Label>
                                  <Input id="signupEmail" type="email" value={form.email} onChange={handleChange} placeholder="이메일을 입력하세요" className="h-12 rounded-xl" required />
                              </div>

                              {/* 비밀번호 영역 */}
                              <div className="space-y-2">
                                  <Label htmlFor="signupPassword">비밀번호</Label>
                                  <Input id="signupPassword" type="password" value={form.password} onChange={handleChange} placeholder="비밀번호 입력" className="h-12 rounded-xl" required />
                                  {passwordError && <p className="text-xs text-red-500 mt-1">{passwordError}</p>}
                              </div>

                              <div className="space-y-2">
                                  <Label htmlFor="signupPasswordConfirm">비밀번호 확인</Label>
                                  <Input id="signupPasswordConfirm" type="password" value={form.passwordConfirm} onChange={handleChange} placeholder="비밀번호 확인" className="h-12 rounded-xl" required />
                                  {form.passwordConfirm && form.password !== form.passwordConfirm && (
                                    <p className="text-xs text-red-500 mt-1">비밀번호가 일치하지 않습니다.</p>
                                  )}
                              </div>

                              {/* 이름 및 휴대폰 영역 */}
                              <div className="space-y-2">
                                  <Label htmlFor="signupMemberName">이름</Label>
                                  <Input id="signupMemberName" value={form.memberName} onChange={handleChange} placeholder="이름을 입력하세요" className="h-12 rounded-xl" required />
                              </div>

                              <div className="space-y-2">
                                  <Label htmlFor="signupPhone">휴대폰 번호</Label>
                                  <Input id="signupPhone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" className="h-12 rounded-xl" />
                              </div>

                              {/* 주소 검색 영역 */}
                              <div className="space-y-2 md:col-span-2">
                                  <Label htmlFor="signupAddress1">주소</Label>
                                  <div className="flex gap-2 mb-2">
                                      <Input value={form.zipcode} placeholder="우편번호" className="h-12 w-32 rounded-xl bg-slate-100" readOnly required />
                                      <Button type="button" variant="outline" className="h-12 rounded-xl shrink-0" onClick={handleSearchAddress}>
                                          주소 검색
                                      </Button>
                                  </div>
                                  <Input id="signupAddress1" value={form.address1} placeholder="기본 주소" className="h-12 rounded-xl bg-slate-100 mb-2" readOnly required />
                                  <Input id="signupAddress2" value={form.address2} onChange={handleChange} placeholder="상세 주소를 입력하세요 (예: 101동 202호)" className="h-12 rounded-xl" />
                              </div>

                              {/* 약관 동의 */}
                              <div className="space-y-3 md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                      <input type="checkbox" className="h-4 w-4" required />
                                      <span>[필수] 이용약관에 동의합니다.</span>
                                  </label>
                                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                      <input type="checkbox" className="h-4 w-4" required />
                                      <span>[필수] 개인정보 수집 및 이용에 동의합니다.</span>
                                  </label>
                              </div>

                              <div className="md:col-span-2">
                                  <Button type="submit" disabled={loading} className="h-12 w-full rounded-2xl bg-slate-900 text-sm font-semibold hover:bg-slate-800 text-white">
                                      {loading ? "가입 처리중..." : "회원가입"}
                                  </Button>
                              </div>
                          </form>

                          <div className="mt-6 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">
                              이미 회원이신가요? <Link to="/login" className="font-medium text-slate-900 hover:underline">로그인</Link>
                          </div>
                      </CardContent>
                  </Card>
              </div>
          </main>
          <Footer />
      </div>
    )
}